import {
    forwardRef,
    useRef,
    useImperativeHandle,
    useCallback, ReactNode, MutableRefObject, useEffect, useContext
} from 'react'
import { Rect } from '../../types/diagram.ts'
import { getRelativeMousePoint } from '../../utils/svg.ts'
import { Point } from './diagram/point.ts'
import { CustomizeDiagramViewPortContext, DiagramViewPortContext } from './DiagramViewPortContext.tsx'

export type InteractionWrapHandle = {
    reset: () => void,
    setCustomViewBox: (x: number, y: number, width: number, height: number) => void,
    currentViewBox: () => { x: number, y: number, width: number, height: number },
    isPanning: () => boolean
}
type InteractionWrapProps = {
    height: number,
    width: number,
    children: ReactNode[]
}
export const InteractionWrap = forwardRef(({
    children,
    height,
    width
}: InteractionWrapProps, ref) => {
    const {
        setViewPort,
        setInitialViewPort,
        setContainer,
        setHTMLContainer,
        setZoom
    } = useContext(CustomizeDiagramViewPortContext)
    const {
        zoom,
        viewPort,
        initialViewPort
    } = useContext(DiagramViewPortContext)
    const container = useRef<HTMLDivElement>() as MutableRefObject<HTMLDivElement>
    const svg = useRef<SVGSVGElement>() as MutableRefObject<SVGSVGElement>
    const isPanning = useRef<boolean>(false)
    const committedMove = useRef<boolean>(false)

    useEffect(() => {
        setContainer(() => svg.current)
        setHTMLContainer(() => container.current)
    }, [setContainer, setHTMLContainer, svg])

    const resetViewBox = useCallback(() => {
        setViewPort(() => ({ x: 0, y: 0, width, height }))
        setZoom(() => 1)
    }, [setZoom, height, width, setViewPort])

    const setCustomViewBox = useCallback((x: number, y: number, width: number, height: number) => {
        if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(width) || !Number.isFinite(height)) {
            return
        }

        const viewPort = { x, y, width, height }
        setViewPort(() => viewPort)
        setInitialViewPort(() => viewPort)
        setZoom(() => 1)
    }, [setZoom, setViewPort, setInitialViewPort])

    useImperativeHandle(ref, (): InteractionWrapHandle => ({
        currentViewBox: () => viewPort,
        reset: resetViewBox,
        setCustomViewBox,
        isPanning: () => committedMove.current
    }), [viewPort, resetViewBox, setCustomViewBox])

    const zoomTo = useCallback((x: number, y: number, delta: number, level: number) => {
        const centerX = viewPort.x + viewPort.width / 2
        const centerY = viewPort.y + viewPort.height / 2
        const newCenterX = centerX + (x - centerX) * delta
        const newCenterY = centerY + (y - centerY) * delta
        const scaledHeight = viewPort.height * (zoom / level)
        const scaledWidth = viewPort.width * (zoom / level)
        const deltaHeight = viewPort.height - scaledHeight
        const deltaWidth = viewPort.width - scaledWidth
        const resultPosX = viewPort.x + (newCenterX - centerX) / level + deltaWidth / 2
        const resultPosY = viewPort.y + (newCenterY - centerY) / level + deltaHeight / 2

        const minX = initialViewPort.x
        const minY = initialViewPort.y
        const maxWidth = initialViewPort.width
        const maxHeight = initialViewPort.height

        const newViewBox: Rect = {
            x: Math.max(minX, resultPosX),
            y: Math.max(minY, resultPosY),
            width: Math.min(maxWidth, scaledWidth),
            height: Math.min(maxHeight, scaledHeight)
        }

        setViewPort(() => newViewBox)
        setViewPort(() => newViewBox)
        setZoom(() => level)
    }, [
        initialViewPort.height, initialViewPort.width,
        initialViewPort.x, initialViewPort.y,
        viewPort.width, viewPort.height,
        viewPort.x, viewPort.y,
        zoom,
        setViewPort, setZoom
    ])

    const handleMouseWheel = useCallback((evt: globalThis.WheelEvent) => {
        evt.preventDefault()
        evt.stopPropagation()

        const relativeMousePoint = getRelativeMousePoint(evt.clientX, evt.clientY, svg.current)

        const scaleMult = (evt.deltaY < 0) ? 0.9 : 1.1

        if (scaleMult < 1) {
            zoomTo(relativeMousePoint.x, relativeMousePoint.y, 0.1, zoom + 0.1)
        } else if (zoom > 1) {
            zoomTo(relativeMousePoint.x, relativeMousePoint.y, -0.1, zoom - 0.1)
        }
    }, [zoom, zoomTo])

    const mousePointRef = useRef<Point>()
    const handleMouseDown = useCallback((e: globalThis.MouseEvent) => {
        isPanning.current = true
        committedMove.current = false
        mousePointRef.current = getRelativeMousePoint(e.clientX, e.clientY, svg.current)

        e.stopPropagation?.()
        e.preventDefault?.()
    }, [])

    const handleMouseUp = useCallback((e: globalThis.MouseEvent) => {
        isPanning.current = false
        mousePointRef.current = undefined

        e.stopPropagation?.()
        e.preventDefault?.()
    }, [])

    const handleMouseMove = useCallback((e: globalThis.MouseEvent) => {
        if (!isPanning.current || !mousePointRef.current) {
            return
        }
        committedMove.current = true

        const prevRelativeMousePointX = mousePointRef.current!.x
        const prevRelativeMousePointY = mousePointRef.current!.y
        const currentRelativeMousePoint = getRelativeMousePoint(e.clientX, e.clientY, svg.current)
        const currentRelativeMousePointX = currentRelativeMousePoint.x
        const currentRelativeMousePointY = currentRelativeMousePoint.y

        setViewPort((prevState) => ({
            x: prevState.x - (currentRelativeMousePointX - prevRelativeMousePointX),
            y: prevState.y - (currentRelativeMousePointY - prevRelativeMousePointY),
            width: prevState.width,
            height: prevState.height
        }))

        const svgContainer = container.current
        svgContainer?.removeEventListener('wheel', handleMouseWheel)
        svgContainer?.removeEventListener('mousedown', handleMouseDown)
        svgContainer?.removeEventListener('mousemove', handleMouseMove)
        svgContainer?.removeEventListener('mouseleave', handleMouseUp)
    }, [handleMouseUp, handleMouseDown, setViewPort, handleMouseWheel])

    useEffect(() => {
        const svgContainer = container.current
        svgContainer?.addEventListener("wheel", handleMouseWheel, { passive: false })
        svgContainer?.addEventListener("mouseup", handleMouseUp, { passive: false })
        svgContainer?.addEventListener("mousedown", handleMouseDown, { passive: false })
        svgContainer?.addEventListener("mousemove", handleMouseMove, { passive: false })
        svgContainer?.addEventListener("mouseleave", handleMouseUp, { passive: false })

        return () => {
            svgContainer?.removeEventListener('wheel', handleMouseWheel)
            svgContainer?.removeEventListener('mouseup', handleMouseUp)
            svgContainer?.removeEventListener('mousedown', handleMouseDown)
            svgContainer?.removeEventListener('mousemove', handleMouseMove)
            svgContainer?.removeEventListener('mouseleave', handleMouseUp)
        }
    }, [handleMouseUp, viewPort, handleMouseDown, handleMouseMove, handleMouseWheel])

    const viewBoxString = `${viewPort?.x || 0} ${viewPort?.y || 0} ${viewPort?.width || 0} ${viewPort?.height || 0}`
    return (
        <div className="flex column"
            style={{
                height: '100%',
                overflow: 'hidden'
            }}
            ref={container}
        >
            <svg
                ref={svg}
                viewBox={viewBoxString}
                width={width}
                height={height}
                style={{
                    userSelect: 'none',
                    msUserSelect: 'none',
                }}
            >
                {children}
            </svg>
        </div>
    )
})
